import {
    RequirementKey,
    UnmatchedDoc,
    type CollectedExtraction,
    type CoverageRow,
} from "@common/helpers/documentFulfillment";
import type { MigrationFileType } from "@common/schemas/accounting-integration.schema";
import {
    ACCRUAL_LEDGER_MISSING_MESSAGE,
    ACCRUAL_NOT_NEEDED_MESSAGE,
    BASIS_REQUIRED_MESSAGE,
    DUPLICATE_COA_MESSAGE,
    NO_TRANSACTIONS_MESSAGE,
    OUTSIDE_PERIOD_MESSAGE,
    OVERLAP_MESSAGE,
    type MigrationDoc,
    type MigrationMatchedRow,
    type MigrationRowStatus,
    type MigrationStatusText,
    type MigrationUnverifiedDoc,
} from "./migrationFileMatching.types";
import {
    extractionWarningsStatusText,
    remainingParserWarnings,
} from "./migrationExtractionWarnings";

const FILE_TYPES: MigrationFileType[] = [
    "coa",
    "cashGl",
    "accrualGl",
    "unknownGl",
];

export function toFileType(key: RequirementKey): MigrationFileType {
    const fileType = FILE_TYPES.find((candidate) => candidate === key);
    if (!fileType) {
        throw new Error(`Unexpected requirement key: ${key}`);
    }
    return fileType;
}

function toRequirementKey(doc: MigrationDoc): RequirementKey | null {
    if (doc.role === "unknownGl") {
        return doc.chosenBasis ?? null;
    }
    return doc.role ?? null;
}

export function toCollectedExtraction(doc: MigrationDoc): CollectedExtraction {
    return {
        documentId: doc.documentId,
        fileName: doc.fileName,
        key: toRequirementKey(doc),
        period: doc.period ?? null,
        status: doc.status,
        summary: doc.summary,
        statusMessage: doc.statusMessage,
    };
}

function toRowStatus(status: CoverageRow["status"]): MigrationRowStatus {
    switch (status) {
        case "ready":
            return "ready";
        case "has_issues":
            return "has_issues";
        case "awaiting":
        case "waived":
            return "awaiting";
        default: {
            const exhaustive: never = status;
            return exhaustive;
        }
    }
}

export function toMatchedRow({
    row,
    docsById,
}: {
    row: CoverageRow;
    docsById: Map<number, MigrationDoc>;
}): MigrationMatchedRow {
    const matched: MigrationMatchedRow = {
        key: row.key,
        role: toFileType(row.requirementKey),
        period: row.period,
        status: toRowStatus(row.status),
    };
    if (row.fileName != null) {
        matched.fileName = row.fileName;
    }
    if (row.summary != null) {
        matched.summary = row.summary;
    }
    if (row.statusMessage != null) {
        matched.statusMessage = row.statusMessage;
    }
    if (row.documentId != null) {
        matched.documentId = row.documentId;
        matched.isUnknownBasis =
            docsById.get(row.documentId)?.role === "unknownGl";
    }
    return matched;
}

export function toBasisRequiredUnverified(
    doc: UnmatchedDoc,
): MigrationUnverifiedDoc {
    return {
        documentId: doc.documentId,
        fileName: doc.fileName,
        status: "basis_required",
        period: doc.period ?? null,
        message: BASIS_REQUIRED_MESSAGE,
        isUnknownBasis: true,
    };
}

// Only a label-less GL gets the basis picker. A roleless extracted doc also
// lands in `needs_dimension`, but offering it a GL basis would be misleading,
// so it stays a plain unrecognized row instead.
export function isAwaitingBasisSelection({
    doc,
    docsById,
}: {
    doc: UnmatchedDoc;
    docsById: Map<number, MigrationDoc>;
}): boolean {
    return (
        doc.reason === "needs_dimension" &&
        docsById.get(doc.documentId)?.role === "unknownGl"
    );
}

function isChosenBasisNotNeeded({
    doc,
    docsById,
}: {
    doc: UnmatchedDoc;
    docsById: Map<number, MigrationDoc>;
}): boolean {
    return (
        doc.reason === "not_needed" &&
        docsById.get(doc.documentId)?.role === "unknownGl"
    );
}

function toUnverifiedMessage({
    doc,
    docsById,
}: {
    doc: UnmatchedDoc;
    docsById: Map<number, MigrationDoc>;
}): MigrationStatusText | undefined {
    if (doc.status === "extracting") {
        return undefined;
    }
    if (doc.statusMessage) {
        return doc.statusMessage;
    }
    switch (doc.reason) {
        case "overlap":
            return OVERLAP_MESSAGE;
        case "outside_period":
            return OUTSIDE_PERIOD_MESSAGE;
        case "no_transactions":
            return NO_TRANSACTIONS_MESSAGE;
        case "duplicate":
            return docsById.get(doc.documentId)?.role === "coa"
                ? DUPLICATE_COA_MESSAGE
                : OVERLAP_MESSAGE;
        case "not_needed":
            return isChosenBasisNotNeeded({ doc, docsById })
                ? ACCRUAL_LEDGER_MISSING_MESSAGE
                : ACCRUAL_NOT_NEEDED_MESSAGE;
        default:
            return undefined;
    }
}

export function toParserWarningUnverified(
    doc: MigrationDoc,
): MigrationUnverifiedDoc {
    const remaining = remainingParserWarnings(doc);
    return {
        documentId: doc.documentId,
        fileName: doc.fileName,
        status: "has_warnings",
        period: doc.period ?? null,
        message: extractionWarningsStatusText(remaining),
    };
}

export function toUnverifiedDoc({
    doc,
    docsById,
}: {
    doc: UnmatchedDoc;
    docsById: Map<number, MigrationDoc>;
}): MigrationUnverifiedDoc {
    const message = toUnverifiedMessage({ doc, docsById });
    const unverified: MigrationUnverifiedDoc = {
        documentId: doc.documentId,
        fileName: doc.fileName,
        status: doc.status,
        period: doc.period ?? null,
    };
    if (message) {
        unverified.message = message;
    }
    const rejection = docsById.get(doc.documentId)?.rejection;
    if (rejection) {
        unverified.rejection = rejection;
    }
    if (isChosenBasisNotNeeded({ doc, docsById })) {
        unverified.isUnknownBasis = true;
    }
    return unverified;
}

export function sortUnverifiedDocsByUploadOrder({
    unverifiedDocs,
    docs,
}: {
    unverifiedDocs: MigrationUnverifiedDoc[];
    docs: MigrationDoc[];
}): MigrationUnverifiedDoc[] {
    const uploadOrderByDocumentId = new Map(
        docs.map((doc, index) => [doc.documentId, index]),
    );
    return [...unverifiedDocs].sort((left, right) => {
        const leftOrder =
            uploadOrderByDocumentId.get(left.documentId) ??
            Number.MAX_SAFE_INTEGER;
        const rightOrder =
            uploadOrderByDocumentId.get(right.documentId) ??
            Number.MAX_SAFE_INTEGER;
        return leftOrder - rightOrder;
    });
}
